/**
 * Brett's keyboard convention for date fields: **T is tomorrow, N is now**.
 *
 * Eden's spec, 9 September 2026: *"if date field is selected and user press T key
 * set date = to tomorrow, user press N set date = to today."* It is one of the
 * MVP items because it is what Brett already does by habit in MaintainX, and a
 * date field that ignores it reads as broken to him rather than as different.
 *
 * ── Why the resolution is PURE and lives here ────────────────────────
 *
 * Every rule below is a calendar rule, not a DOM rule, and each one has a way of
 * being got wrong that a rendering test would not catch:
 *
 * - **The day is the user's LOCAL calendar day, never UTC.** `toISOString()`
 *   would be the obvious way to format a date input's value and it is wrong for
 *   half of every day in every US zone: at 19:00 in America/Chicago the UTC date
 *   is already tomorrow, so "N" would set tomorrow and "T" the day after. The
 *   local components are read individually for exactly this reason.
 * - **Month, year and DST rollover.** `new Date(y, m, d + 1)` is the arithmetic
 *   that handles 31 December and the 30th of a 30-day month, because the Date
 *   constructor normalises an out-of-range day itself. Adding 86_400_000 ms does
 *   not: on a spring-forward day that lands at 01:00 on the same calendar date.
 * - **`datetime-local` keeps its TIME.** These fields mean "this instant", and
 *   replacing the whole value would silently reset a time the user had already
 *   set. Only the date half moves.
 *
 * So the arithmetic is testable without a DOM, and `now` is a parameter rather
 * than a call to `new Date()` inside — a function that reads the clock itself can
 * only be tested on the day the test runs.
 */

/** The two input types the shortcut applies to. */
export type DateFieldKind = "date" | "datetime-local";

/**
 * `YYYY-MM-DD` for a local calendar day `dayOffset` days from `now`.
 *
 * Local components throughout. See the header: UTC is wrong here, not merely
 * imprecise.
 */
export function localDateValue(now: Date, dayOffset: number): string {
  // The constructor normalises an out-of-range day, which is what makes
  // 31 December + 1 work without any month arithmetic of our own.
  const day = new Date(now.getFullYear(), now.getMonth(), now.getDate() + dayOffset);
  const pad = (value: number) => String(value).padStart(2, "0");
  return `${day.getFullYear()}-${pad(day.getMonth() + 1)}-${pad(day.getDate())}`;
}

/** `HH:mm` for the local wall clock at `now`. */
function localTimeValue(now: Date): string {
  const pad = (value: number) => String(value).padStart(2, "0");
  return `${pad(now.getHours())}:${pad(now.getMinutes())}`;
}

/**
 * The time half of a `datetime-local` value, or the current local time when the
 * field is empty or holds something this cannot read.
 *
 * Deliberately lenient about what counts as readable: browsers may include
 * seconds (`14:30:00`) when a `step` is set, and that whole tail is kept. What is
 * NOT kept is a value with no `T` at all, because there is no time in it to
 * preserve.
 */
function timePartOf(current: string, now: Date): string {
  const separator = current.indexOf("T");
  if (separator === -1) return localTimeValue(now);
  const time = current.slice(separator + 1);
  return /^\d{2}:\d{2}(:\d{2}(\.\d+)?)?$/.test(time) ? time : localTimeValue(now);
}

/**
 * How many days from today the key means, or `null` when the key is not one of
 * the shortcuts.
 *
 * Case-insensitive because Eden's spec says "T" and a user holding shift is
 * still asking for tomorrow.
 */
function offsetForKey(key: string): number | null {
  switch (key.toLowerCase()) {
    case "t":
      return 1;
    case "n":
      return 0;
    default:
      return null;
  }
}

/**
 * The value the field should take, or `null` to leave the event alone.
 *
 * `current` is the field's present value, needed only so a `datetime-local` keeps
 * its time. A `null` return means "not a shortcut" and the caller must not
 * `preventDefault` — that distinction is what keeps ordinary typing and the
 * native picker working.
 */
export function resolveDateShortcut(
  key: string,
  kind: DateFieldKind,
  current: string,
  now: Date,
): string | null {
  const offset = offsetForKey(key);
  if (offset === null) return null;
  const date = localDateValue(now, offset);
  return kind === "datetime-local" ? `${date}T${timePartOf(current, now)}` : date;
}

/**
 * Modifier keys that mean the keystroke belongs to the browser or the OS, not to
 * this field.
 *
 * Ctrl/Cmd+N is a new window and Ctrl/Cmd+T a new tab in every browser; Alt+T
 * opens a menu on Windows. Swallowing those would be a worse bug than the one
 * this feature fixes, because it breaks something the user relies on everywhere.
 * Shift is deliberately NOT in this list — Shift+T is how a capital T is typed.
 */
export function hasShortcutBlockingModifier(event: {
  ctrlKey: boolean;
  metaKey: boolean;
  altKey: boolean;
}): boolean {
  return event.ctrlKey || event.metaKey || event.altKey;
}
